import { useEffect, useState } from 'react';
import type { ConnectionState, Me } from '@momomail/shared';
import { bridge, client } from '../lib/bridge';
import { friendlyError, type FriendlyError } from '../lib/friendly-error';
import { t } from '../lib/i18n';
import { Icon } from './Icon';
import { confirmDialog } from './Overlay';
import { Mascot } from './Mascot';
import { toast, toastError } from './Toasts';
import { lastUsername, people, rememberUsername } from '../lib/people';
import { PasswordRow, RecoveryEmailRow } from './UserSettings';

/** Hook for the connection status (WebSocket). */
export function useConnectionState(): ConnectionState {
  const [state, setState] = useState(bridge.connection.state());
  useEffect(() => bridge.connection.onState(setState), []);
  return state;
}

/** A small notice below the title bar while disconnected from the backend; disappears once connected. */
export function ConnectionBanner({ state }: { state: ConnectionState }) {
  if (state !== 'reconnecting') return null;
  const local = bridge.connection.info().mode === 'local';
  return (
    <div className="conn-banner" role="status">
      <span className="spinner" />
      <span>{local ? t('本机邮件服务断开了，正在重新连接…') : t('和邮件服务器的连接断了，正在重新连接…')}</span>
      <button className="btn btn--small btn--quiet" onClick={() => client.reconnectNow()}>
        <Icon name="refresh" size={14} /> {t('现在重试')}
      </button>
    </div>
  );
}

/** Before the first connection: a spinner; if the local backend fails to start, show the reason and a retry. */
export function ConnectingScreen() {
  const info = bridge.connection.info();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <main className="welcome login">
      <div className="welcome__card login__card">
        <div className="login__art" aria-hidden>
          <Mascot />
        </div>
        <h1 className="welcome__title">{info.error ? t('邮件服务没能启动') : t('正在连接邮件服务…')}</h1>
        {info.error ? <FormProblem problem={{ ...friendlyError(info.error), field: null }} /> : <span className="spinner spinner--big" />}
        {(slow || info.error) && (
          <p className="muted">
            {info.mode === 'local' ? t('本机的邮件服务还没响应。') : t('连不上 {url}。', { url: info.url })}
            <button className="field__link login__inline" onClick={() => location.reload()}>
              <Icon name="refresh" size={13} /> {t('重新加载')}
            </button>
          </p>
        )}
        {bridge.desktop && info.mode === 'remote' && (
          <button className="field__link" onClick={() => void bridge.connection.useLocal()}>
            {t('改回本机模式')}
          </button>
        )}
      </div>
    </main>
  );
}

/** Login error: one line shown under the relevant input. */
function FieldProblem({ problem, field }: { problem: FriendlyError | null; field: FriendlyError['field'] }) {
  if (!problem || problem.field !== field) return null;
  return (
    <span className="field__error" role="alert">
      <Icon name="alert" size={14} /> {problem.title}
    </span>
  );
}

/** Login error unrelated to any input (no network, server error, incompatible version): a notice block in the form, with raw details under "详情" (details). */
function FormProblem({ problem }: { problem: FriendlyError | null }) {
  const [open, setOpen] = useState(false);
  if (!problem || problem.field) return null;
  return (
    <div className={`login__problem ${problem.severity === 'warn' ? 'login__problem--warn' : ''}`} role="alert">
      <strong>
        <Icon name="alert" size={15} /> {problem.title}
      </strong>
      <span>{problem.hint}</span>
      {problem.technical && problem.detail && (
        <>
          <button type="button" className="field__link" onClick={() => setOpen(!open)} aria-expanded={open}>
            {t('详情')}
          </button>
          {open && <pre className="toast__detail">{problem.detail}</pre>}
        </>
      )}
    </div>
  );
}

/**
 * On login errors, explain in plain language in the form. If it's an input's fault (address, password), note it under that input and also pop a toast with a suggestion;
 * if it's not an input's fault (can't connect, server error), put a notice box with details in the form and don't pop a duplicate toast.
 * hasUrlField: whether the form has a server address field (the web app doesn't).
 */
function useLoginProblem(hasUrlField = true) {
  const [problem, setProblem] = useState<FriendlyError | null>(null);
  return {
    problem,
    fail(error: unknown) {
      const p = friendlyError(error);
      setProblem(p);
      if (p.field && (hasUrlField || p.field !== 'url')) toastError(error, 'login');
    },
    clear: (field?: FriendlyError['field']) => setProblem((p) => (!field || p?.field === field ? null : p)),
  };
}

/** Login page for a remote server / the web app. */
export function LoginScreen({ onForgot }: { onForgot: () => void }) {
  const info = bridge.connection.info();
  const [url, setUrl] = useState(info.mode === 'browser' ? location.origin : info.url);
  const [username, setUsername] = useState(lastUsername);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { problem, fail, clear } = useLoginProblem(info.mode !== 'browser');
  /** Whether the server supports self-service password reset (null = unknown yet / unreachable). */
  const [selfReset, setSelfReset] = useState<boolean | null>(null);
  useEffect(() => {
    // Desktop remote mode can only reach already-configured addresses (CSP), so don't check a newly entered address yet
    if (!info.url && info.mode !== 'browser') return;
    people.health(info.mode === 'browser' ? location.origin : info.url).then(
      (h) => setSelfReset(h.selfReset),
      () => setSelfReset(null),
    );
  }, [info.url, info.mode]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clear();
    try {
      rememberUsername(username);
      await bridge.connection.login(url, username.trim(), password);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const forgot = () => {
    // The desktop app doesn't have these pages: open the server's "忘记密码" (forgot password) in the browser
    if (bridge.desktop) void bridge.mail.openExternal(`${url.replace(/\/+$/, '')}/#/forgot`);
    else onForgot();
  };
  return (
    <main className="welcome login">
      <form className="welcome__card login__card" onSubmit={(e) => void submit(e)}>
        <div className="login__art" aria-hidden>
          <Mascot />
        </div>
        <h1 className="welcome__title">{t('登录馍馍邮箱服务器')}</h1>
        <p className="welcome__lead">{t('邮件由你自己的馍馍邮箱服务器收取和保存；这里用管理员给你开的账号登录。')}</p>
        {info.mode !== 'browser' && (
          <label className="field">
            <span className="field__label">{t('服务器地址')}</span>
            <input value={url} onChange={(e) => (setUrl(e.target.value), clear('url'))} placeholder="https://mail.example.com" required autoFocus={!url} aria-invalid={problem?.field === 'url'} />
            <FieldProblem problem={problem} field="url" />
          </label>
        )}
        <label className="field">
          <span className="field__label">{t('用户名')}</span>
          <input value={username} onChange={(e) => (setUsername(e.target.value), clear('username'))} autoComplete="username" required autoFocus={!!url && !username} aria-invalid={problem?.field === 'username'} />
          <FieldProblem problem={problem} field="username" />
        </label>
        <label className="field">
          <span className="field__label">{t('密码')}</span>
          <input type="password" value={password} onChange={(e) => (setPassword(e.target.value), clear('password'))} autoComplete="current-password" required autoFocus={!!url && !!username} aria-invalid={problem?.field === 'password'} />
          <FieldProblem problem={problem} field="password" />
        </label>
        {/* The web app has no server address field: address-related problems go here too */}
        <FormProblem problem={problem && (info.mode === 'browser' && problem.field === 'url' ? { ...problem, field: null } : problem)} />
        <div className="login__actions">
          <button className="btn btn--primary" type="submit" disabled={busy || !username.trim() || !password}>
            {busy ? <span className="spinner" /> : <Icon name="lock" size={16} />}
            {t('登录')}
          </button>
          {selfReset ? (
            <button type="button" className="field__link" onClick={forgot}>
              {t('忘记密码？')}
            </button>
          ) : (
            <span className="muted">{t('忘记密码请联系管理员')}</span>
          )}
          {bridge.desktop && (
            <button type="button" className="btn btn--quiet" onClick={() => void bridge.connection.useLocal()} disabled={busy}>
              {t('改回本机模式')}
            </button>
          )}
        </div>
      </form>
    </main>
  );
}

/**
 * "账号与连接" (Account & connection) in settings:
 * - Signed in to a server (desktop remote mode, web app): shows "已登录为 某某" (signed in as X), followed by switch account / sign out / back to local mode,
 *   and below that, recover email and change password (expand on click);
 * - Desktop local mode: explains it's in local mode; "连接远程服务器" (connect to remote server) expands the login form on click.
 * The login form is collapsed by default: otherwise a login box sitting there while signed in makes it look like you're not signed in.
 */
export function ConnectionSettings({ me, onMe }: { me: Me | null; onMe: (m: Me) => void }) {
  const info = bridge.connection.info();
  const [switching, setSwitching] = useState(false);
  const signedIn = info.mode !== 'local' && !!me && !me.local;
  const server = info.mode === 'browser' ? location.origin : info.url;
  const logout = async () => {
    if ((await confirmDialog({ title: t('退出登录？'), message: t('这台设备会忘掉服务器的登录状态，服务器上的邮件和账户不受影响。'), confirm: t('退出登录') })) !== 'confirm') return;
    await bridge.connection.logout();
    toast(t('已退出登录'), 'ok');
  };
  const useLocal = async () => {
    if ((await confirmDialog({ title: t('改回本机模式？'), message: t('这台电脑会退出服务器的登录，改用本机的邮件服务（本机原来的数据都还在）。'), confirm: t('改回本机') })) !== 'confirm') return;
    await bridge.connection.useLocal();
  };
  return (
    <div className="settings__section">
      <h3 className="settings__h">{t('账号与连接')}</h3>
      {info.mode === 'local' ? (
        <div className="setting">
          <div className="setting__text">
            <div className="setting__title">{t('本机模式')}</div>
            <div className="setting__desc muted">{t('邮件服务跑在这台电脑上，不用登录。也可以连接部署在自己服务器上的馍馍邮箱。')}</div>
          </div>
          {!switching && (
            <div className="setting__control">
              <button className="btn btn--small" onClick={() => setSwitching(true)}>
                <Icon name="external" size={14} /> {t('连接远程服务器')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="setting">
          <span className="conn-avatar" aria-hidden>
            {(me?.username ?? '?').slice(0, 1).toUpperCase()}
          </span>
          <div className="setting__text">
            <div className="setting__title">{signedIn ? t('已登录为 {name}', { name: me!.username }) : t('已连接服务器')}</div>
            <div className="setting__desc muted conn-url">
              {signedIn && (me!.isAdmin ? t('管理员') : t('普通用户'))}
              {signedIn && ' · '}
              {server}
            </div>
          </div>
        </div>
      )}
      {info.mode !== 'local' && !switching && (
        <div className="btn-row conn-actions">
          {bridge.desktop && (
            <button className="btn btn--small" onClick={() => setSwitching(true)}>
              <Icon name="users" size={14} /> {t('更换账户')}
            </button>
          )}
          <button className="btn btn--small btn--quiet" onClick={() => void logout()}>
            <Icon name="power" size={14} /> {t('退出登录')}
          </button>
          {bridge.desktop && (
            <button className="btn btn--small btn--quiet" onClick={() => void useLocal()}>
              <Icon name="monitor" size={14} /> {t('改回本机模式')}
            </button>
          )}
        </div>
      )}
      {switching && <SwitchForm initialUrl={info.mode === 'remote' ? info.url : ''} local={info.mode === 'local'} onCancel={() => setSwitching(false)} />}
      {signedIn && !switching && (
        <>
          <RecoveryEmailRow me={me!} onMe={onMe} />
          <PasswordRow />
        </>
      )}
    </div>
  );
}

/** Desktop: sign in to another account / another server (on success the whole UI switches over; in local mode, the local mail service stops once connected). */
function SwitchForm({ initialUrl, local, onCancel }: { initialUrl: string; local: boolean; onCancel: () => void }) {
  const [url, setUrl] = useState(initialUrl);
  const [username, setUsername] = useState(local ? lastUsername : '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { problem, fail, clear } = useLoginProblem();
  const connect = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clear();
    try {
      rememberUsername(username);
      await bridge.connection.login(url, username.trim(), password);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <form className="conn-form setting-expand" onSubmit={(e) => void connect(e)}>
      <p className="setting__title">{local ? t('连接远程服务器') : t('登录另一个账户')}</p>
      <p className="muted setting__desc">
        {local
          ? t('把馍馍邮箱服务器部署在自己的服务器上（见 README），这里填地址和管理员给你开的用户名、密码。连接后这台电脑上的本机邮件服务会停下，本机数据保留，随时可以改回来。')
          : t('可以换一个用户名，也可以换一台服务器。登录成功后这台电脑就切换过去，原来的账户在服务器上不受影响。')}
      </p>
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('服务器地址')}</span>
          <input value={url} onChange={(e) => (setUrl(e.target.value), clear('url'))} placeholder="https://mail.example.com" required aria-invalid={problem?.field === 'url'} />
          <FieldProblem problem={problem} field="url" />
        </label>
        <label className="field">
          <span className="field__label">{t('用户名')}</span>
          <input value={username} onChange={(e) => (setUsername(e.target.value), clear('username'))} autoComplete="username" required autoFocus={!local} aria-invalid={problem?.field === 'username'} />
          <FieldProblem problem={problem} field="username" />
        </label>
        <label className="field">
          <span className="field__label">{t('密码')}</span>
          <input type="password" value={password} onChange={(e) => (setPassword(e.target.value), clear('password'))} autoComplete="current-password" required aria-invalid={problem?.field === 'password'} />
          <FieldProblem problem={problem} field="password" />
        </label>
      </div>
      <FormProblem problem={problem} />
      <div className="btn-row">
        <button className="btn btn--small btn--primary" type="submit" disabled={busy || !url || !username.trim() || !password}>
          {busy ? <span className="spinner" /> : <Icon name="lock" size={14} />} {local ? t('登录并切换') : t('登录')}
        </button>
        <button className="btn btn--small btn--quiet" type="button" onClick={onCancel} disabled={busy}>
          {t('取消')}
        </button>
      </div>
    </form>
  );
}
